import { produce } from "immer";

import { generateContent } from "@app/services/zoltar";
import { fetchPersonaImages } from "@app/services/adobeFirefly";

import { queryClient } from "./query";
import { storyKey } from "@app/hooks/stories";

import { importAllPersonas } from "@app/utils";

/**
 * Generates personas for the given story.
 *
 * @param {string} storyId - The ID of the story.
 *
 * @returns {Promise<void>} - A promise that resolves when the personas are generated.
 * @throws {Error} If the required data is missing or if the generation fails.
 */
export const generatePersonas = async (storyId, get, set) => {
  set(
    { isPromptPersonasPending: true },
    undefined,
    "promptPersonas/generatePersonas",
  );

  get().addToast(`Generating personas details`, {
    variant: "info",
    timeout: -1,
  });

  try {
    const ims = get().ims;
    const story = get().getStory(storyId);

    if (!ims || !story.industry) {
      throw new Error("Missing required data to generate personas");
    }

    const personas = await importAllPersonas();

    const personasPromptTemplate = `Here are 2 documents: a customer journey story script in JSON format from ${story.industry} industry and a personas list in JSON format.

In the story script there's array "personas". For each persona write a prompt that will be used to describe the persona to generate their image – "descriptionPrompt". Include details of a person's appearance, ethnicity, characteristics, and detailed description of clothing.

Next, write a prompt that will be used to generate a profile portrait for this persona – "avatarPrompt". Portrait should be positioned in the center of the frame with a face in a front view. Include "descriptionPrompt" in this prompt. At the end of the prompt add "Neutral background. Professional photography style."

Next, for each persona in the story, match them with one of the existing personas from the personas list using the "descriptionPrompt" that you've just generated.

Consider the following matching criteria:
1. Gender compatibility (if story says it's female, find female persona, if story says it's male, find male persona)
2. Age range similarity (if story says it's 30-40 years old, find persona in the 30-40 age range)
3. Role/profession alignment (if story says it's a customer, find a persona with "Customer" role)
4. Description characteristics that match the descriptionPrompt (if story says it's a customer, find a persona with "Customer" role)

For each match, use the "id" from the personas list as "libraryId" and the "avatarId" from the personas list as "avatarId" for the story persona. Take the "id" and "avatarId" exactly as they are in the personas list, do not make them up nor change them.

Take into consideration context of all the data in the provided script. Adjust the prompts to represent specified "industry" as well as to be aligned with the whole story script.

Don't use personas names in the prompts, but rather use their gender.`;

    const promptPersonasResponse = await generateContent(
      {
        user: ims.profile.email,
        data: {},
        prompt_template: [
          {
            prompt_template: JSON.stringify(story),
            role: "system",
          },
          {
            prompt_template: JSON.stringify(personas),
            role: "system",
          },
          {
            prompt_template:
              "You are a prompt engineering expert specializing in image prompting.",
            role: "system",
          },
          {
            prompt_template: personasPromptTemplate,
            role: "user",
          },
          {
            prompt_template:
              "You are an assistant designed to output JSON. Please make sure to output JSON only.",
            role: "system",
          },
        ],
      },
      "Failed to fetch personas prompt",
    );

    console.log("promptPersonasResponse:", promptPersonasResponse);

    set(
      {
        promptPersonasResponse,
        isPromptPersonasPending: false,
      },
      undefined,
      "promptPersonas/generatePersonas",
    );

    await get().parsePromptPersonasResponse(storyId, promptPersonasResponse);
  } catch (error) {
    console.error("Error generating personas:", error);
    set(
      { isPromptPersonasPending: false, shouldGenerateImages: false },
      undefined,
      "promptPersonas/generatePersonas",
    );

    get().addToast(`Failed to generate personas details`, {
      variant: "negative",
      timeout: -1,
      actionLabel: "Retry",
      shouldCloseOnAction: true,
      onAction: () => {
        get().generatePersonas(storyId);
      },
    });
  }
};

/**
 * Parses the response from the personas prompt and updates the story with the generated persona details.
 *
 * @param {string} storyId
 * @param {*} promptPersonasResponse
 *
 * @returns {Promise<void>} - A promise that resolves when the personas are parsed and updated in the story.
 * @throws {Error} If the response is invalid or if the parsing fails.
 */
export const parsePromptPersonasResponse = async (
  storyId,
  promptPersonasResponse,
  get,
  set,
) => {
  try {
    if (promptPersonasResponse?.chat?.choices?.[0]?.finishReason !== "stop") {
      // TODO: handle LLM error response
      throw new Error(
        "Failed to fetch personas prompt",
        promptPersonasResponse,
      );
    }

    const personasContext =
      JSON.parse(
        promptPersonasResponse?.chat?.choices?.[0]?.message?.content ||
          '{"personas": []}',
      )?.personas || [];

    console.log({ personasContext });

    // set(
    //   produce((state) => {
    //     personasContext.forEach((persona, idx) => {
    //       const storyIndex = state.stories.findIndex(
    //         (story) => story.id === storyId,
    //       );
    //       if (storyIndex === -1) {
    //         throw new Error("Story not found");
    //       }
    //       state.stories[storyIndex].personas[
    //         persona?.id ?? idx
    //       ].avatarPrompt = persona.avatarPrompt;
    //       state.stories[storyIndex].personas[
    //         persona?.id ?? idx
    //       ].descriptionPrompt = persona.descriptionPrompt;
    //       state.stories[storyIndex].personas[persona?.id ?? idx].libraryId =
    //         persona.libraryId;
    //       state.stories[storyIndex].personas[persona?.id ?? idx].avatarId =
    //         persona.avatarId;
    //     });
    //   }),
    //   undefined,
    //   "promptPersonas/parsePromptPersonasResponse",
    // );

    queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
      produce(oldData, (state) => {
        personasContext.forEach((persona, idx) => {
          state.personas[persona?.id ?? idx].avatarPrompt =
            persona.avatarPrompt;
          state.personas[persona?.id ?? idx].descriptionPrompt =
            persona.descriptionPrompt;
          state.personas[persona?.id ?? idx].libraryId = persona.libraryId;
          state.personas[persona?.id ?? idx].avatarId = persona.avatarId;
        });
      }),
    );
  } catch (error) {
    console.error("Error parsing personas response:", error);
    get().addToast(`Failed to generate personas details`, {
      variant: "negative",
      timeout: -1,
      actionLabel: "Retry",
      shouldCloseOnAction: true,
      onAction: () => {
        get().generatePersonas(storyId);
      },
    });
  }
};

export const createPromptPersonasSlice = (set, get) => ({
  isPromptPersonasPending: false,
  promptPersonasResponse: null,
  generatePersonas: async (storyId) => generatePersonas(storyId, get, set),
  parsePromptPersonasResponse: async (storyId, promptPersonasResponse) =>
    parsePromptPersonasResponse(storyId, promptPersonasResponse, get, set),
  /**
   * Generates images for the personas in the given story.
   *
   * @param {string} storyId - The ID of the story.
   *
   * @returns {Promise<void>} - A promise that resolves when the persona images are generated.
   * @throws {Error} If the generation fails or if the story or personas are not found.
   */
  generatePersonasImages: async (storyId) => {
    const story = get().getStory(storyId);
    const ims = get().ims;

    let counter = 0;

    // get().addToast(
    //   `Generating personas images: ${counter}/${story?.personas?.length}`,
    //   {
    //     variant: "info",
    //     timeout: -1,
    //   },
    // );

    try {
      await get().fetchImageReference(storyId);

      await Promise.allSettled(
        story?.personas?.map((persona, idx) =>
          fetchPersonaImages(storyId, persona, {
            uploadId: get().imageReferenceResponse?.id,
            // url: get().imageReference?.internalUrl,
          }).then((personaImages) => {
            console.log("persona images:", idx, personaImages);

            counter += 1;

            // get().addToast(
            //   `Generating personas images: ${counter}/${story?.personas?.length}`,
            //   {
            //     variant: "info",
            //     timeout: -1,
            //   },
            // );

            // set(
            //   produce((state) => {
            //     const storyIndex = state.stories.findIndex(
            //       (story) => story.id === storyId,
            //     );
            //     if (storyIndex === -1) {
            //       throw new Error("Story not found");
            //     }

            //     state.stories[storyIndex].personas[idx].avatarUrl =
            //       personaImages[0]?.[0]?.url;
            //     state.stories[storyIndex].personas[idx].avatarSeed =
            //       personaImages[0]?.[0]?.seed;
            //     state.stories[storyIndex].personas[idx].avatarPath =
            //       personaImages[0]?.[0]?.path;
            //   }),
            //   undefined,
            //   "promptPersonas/parsePromptPersonasResponse",
            // );

            queryClient.setQueryData(storyKey.detail(storyId), (oldData) =>
              produce(oldData, (state) => {
                state.personas[idx].avatarUrl = personaImages[0]?.[0]?.url;
                state.personas[idx].avatarSeed = personaImages[0]?.[0]?.seed;
                state.personas[idx].avatarPath = personaImages[0]?.[0]?.path;
              }),
            );
          }),
        ),
      );
    } catch (error) {
      console.error("Error generating personas images:", error);

      get().addToast(`Failed to generate personas images`, {
        variant: "negative",
        timeout: -1,
        actionLabel: "Retry",
        shouldCloseOnAction: true,
        onAction: () => {
          get().generatePersonasImages(storyId);
        },
      });
    }
  },
});
